import type { FindCreateOrderDatesQuery } from '../../../api/order/create-available-dates';
import type { SingleProductQuery } from '../../../api/single-product';
import type { DayOfWeek } from '../../../api/subscription/dto/get-my-subscriptions.dto';
import type { ProductType } from '../../../type';

export const INVALIDATE_KEY = {
  DIARY: 'DIARY',
};

const getFastestDeliveryDate = (...args: unknown[]) => [
  'getFastestDeliveryDate',
  ...args,
];

const getSingleProductQuery = (params?: SingleProductQuery) => [
  'getSingleProductQuery',
  params,
];

const getDefaultVegetavles = (
  year: number,
  week: number,
  productId: number,
  day: DayOfWeek,
) => ['getDefaultVegetavles', year, week, productId, day];

const getAllAllergiesToBan = (allergies?: string[]) => [
  'getAllAllergiesToBan',
  allergies,
];

const checkRecipeCreatorName = (reatorName: string | undefined) => [
  'checkRecipeCreatorName',
  reatorName,
];

const getSingleProduct = (id?: number) => ['getSingleProduct', id];

const getSingleOrder = (id?: number) => ['getSingleOrder', id];

const getSingleOrderById = (id?: number) => ['getSingleOrderById', id];

const getSingleOrderByIdWithReview = (id?: number) => [
  'getSingleOrderByIdWithReview',
  id,
];

const getSingleOrderSearch = (uuid: string | undefined) => [
  'getSingleOrderSearch',
  uuid,
];

const getCreateAvailableDates = (param: FindCreateOrderDatesQuery) => [
  'getCreateAvailableDates',
  param,
];

const getOrderById = (orderId?: number) => ['get-order-by-id', orderId];

const getCanPostponeDates = (id?: number, from?: string, to?: string) => [
  'getCanPostponeDates',
  id,
  from,
  to,
];

const getSubscriptionById = (id?: number) => ['getSubscriptionById', id];

const getPost = (id?: number) => ['getPost', id];

const getRecipePaper = (
  year?: number,
  week?: number,
  productType?: ProductType,
) => ['getRecipePaper', year, week, productType];

const getDiariesReferencingRecipe = (id?: number) => [
  INVALIDATE_KEY.DIARY,
  'getDiariesReferencingRecipe',
  id,
];

const getOneDiary = (id: number | undefined) => [
  INVALIDATE_KEY.DIARY,
  'getOneDiary',
  id,
];

const searchDiary = (query: object) => [
  INVALIDATE_KEY.DIARY,
  'searchDiary',
  query,
];

const getDiaryComments = (id: number | undefined) => ['getDiaryComments', id];

const canDawnDelivery = (zipCode: string | undefined) => [
  'canDawnDelivery',
  zipCode,
];

export const QueryKeys = {
  // Ustore
  getSingleProductQuery,
  getSingleProduct,
  getPromotionSingleProduct: ['getPromotionSingleProduct'],
  getReviewCuration: ['getReviewCuration'],
  getAllSingleProducts: ['getAllSingleProducts'],
  getMyDeliveryAddressList: ['getMyDeliveryAddressList'],
  getSingleOrderSearch,

  // subscription
  getSubscriptionCount: ['getSubscriptionCount'],
  getFastestDeliveryDate,
  getCanPostponeDates,
  getSubscriptionById,
  getMySubscriptionsV2: ['getMySubscriptionsV2'],
  getSubscriptionOrder: (params?: unknown) => ['getSubscriptionOrder', params],

  // mypages
  getDefaultVegetavles,
  getAllAllergiesToBan,
  getAllAllergyList: (params: unknown) => ['getAllAllergyList', params],
  checkRecipeCreatorName,

  // order
  getSingleOrder,
  getOrderById,
  getSingleOrderById,
  getSingleOrderByIdWithReview,
  getCreateAvailableDates,
  getOrderNextDeliveryDate: (orderId: number) => [
    'getOrderNextDeliveryDate',
    orderId,
  ],

  // recipe-paper
  getRecipePaper,

  //coupons
  getMyCouponHistory: ['getMyCouponHistory'],
  getMyRepresentativeCoupon: ['getMyRepresentativeCoupon'],

  //point
  getMyPointHistory: ['getMyPointHistory'],

  // credit-card
  getMyCreditCards: ['getMyCreditCards'],
  getMyCreditCardMembership: ['getMyCreditCardMembership'],

  // blog
  getAllBlog: ['getAllBlog'],

  // post
  getAllPosts: ['getAllPosts'],
  getBlogCuration: ['getBlogCuration'],
  getPost,

  // recipe
  getMyNotiFlags: ['getMyNotiFlags'],
  getDiariesReferencingRecipe,
  getOneDiary,
  getMylikes: [INVALIDATE_KEY.DIARY, 'getMylikes'],
  searchDiary,
  getDiaryComments,

  // delivery
  canDawnDelivery,

  // week
  getLastDeliveryWeek: ['getLastDeliveryWeek'],

  // reviews
  getReviewsByReviewType: (...args: unknown[]) => [
    'getReviewsByReviewType',
    ...args,
  ],

  // promotion
  getPromotionComments: ['getPromotionComments'],

  // users
  getMe: (...props: unknown[]) => ['getMe', ...props],
  getMembership: (orderId?: number) => ['getMembership', orderId],
  getUserSegment: ['getUserSegment'],

  // product
  getProductSearch: (params?: unknown) => ['getProductSearch', params],

  // promotion
  getPromotionCanRegister: (params?: unknown) => [
    'getPromotionCanRegister',
    params,
  ],
  getPromotionRegistrationStatus: (params?: unknown) => [
    'getPromotionRegistrationStatus',
    params,
  ],

  // return-request
  getReturnRequest: (orderId: number | undefined) => [
    'getReturnRequest',
    orderId,
  ],

  getOrderItemsByOrderId: (...args: unknown[]) => [
    'getOrderItemsByOrderId',
    ...args,
  ],

  getOrerItemsAndConfirm: (...args: unknown[]) => [
    'getOrerItemsAndConfirm',
    ...args,
  ],

  // subscription-gift
  SubscriptionGiftApi: {
    getGiftById: (id?: number) => ['getGiftById', id],
    getGiftByUuid: (uuid?: string) => ['getGiftByUuid', uuid],
    getMyGiftList: () => ['getMyGiftList'],
    getGiftPayment: (id?: number) => ['getGiftPayment', id],
  },

  payment: {
    getRecentPaymentMethod: () => ['getRecentPaymentMethod'],
  },

  getIsRemoteArea: (zipCode: string | undefined) => [
    'getIsRemoteArea',
    zipCode,
  ],

  // cart
  getCart: (...props: unknown[]) => ['getCart', ...props],
} as const;
